import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ListChecks, Loader2, CheckCircle2, AlertTriangle, Building2,
} from 'lucide-react';

import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';

import PeriodPicker from '../../components/mec/PeriodPicker';
import ActionQueueRow from '../../components/mec/ActionQueueRow';
import { usePeriodState } from '../../components/mec/usePeriodState';

import { listRouting, getClose } from '../../api/mec';
import { formatMoney } from '../../utils/formatters';

// MEC "Today" — the cross-shop action queue (Phase 3 of the UX overhaul).
//
// Surfaces every actionable item across every accessible shop for the
// selected period (defaults to MTD). One row per (shop, action_type)
// where there's work pending. Click drills into
// /mec/location/{shop_id}?start=…&end=…&filter=<bucket-key>.
//
// The filter key is taken from ACTION_DRILLDOWN_FILTER below and matches
// the matched_method strings the drill-down (Phase 4) already supports
// so deep-links survive the implementation handoff.

const ACTION_DRILLDOWN_FILTER = {
  bills_missing:    'tier_1_total_mismatch',
  over_billed:      'ambiguous',
  credits_expected: 'credit_pending',
  memo_gap:         'tier_3_no_po',
  tier_2_pending:   'tier_2',
};

const ACTION_LABEL = {
  bills_missing:    'Bills missing or mismatched',
  over_billed:      'Over-billed by vendor',
  credits_expected: 'Credits expected',
  memo_gap:         'Memo discipline gap (no PO# in QBO)',
  tier_2_pending:   'Tier 2 pending review',
};

// MTD = 1st of current month → today (local-time ISO yyyy-mm-dd).
function pad(n) { return String(n).padStart(2, '0'); }
function iso(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function mtdRange() {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return { start: iso(start), end: iso(today) };
}

// Compute urgency band from dollars + action_type:
//   - high   if > $5,000 OR action_type is bills_missing / over_billed
//   - medium if > $1,000
//   - low    otherwise
function computeUrgency(actionType, dollarsCents) {
  if (actionType === 'bills_missing' || actionType === 'over_billed') return 'high';
  const dollars = (dollarsCents || 0) / 100;
  if (dollars > 5000) return 'high';
  if (dollars > 1000) return 'medium';
  return 'low';
}

// Build the per-shop action rows from a close payload's match_tally.
// Rows with count=0 are filtered out at the caller.
function buildActionsForShop(shop, close, periodStart, periodEnd) {
  const tally = close?.match_tally || close?.parts?.match_tally || {};
  const candidates = [
    {
      action_type: 'bills_missing',
      count: tally.tier_1_total_mismatch || 0,
      dollars_cents: tally.tier_1_cents || 0,
    },
    {
      action_type: 'over_billed',
      count: tally.ambiguous || 0,
      dollars_cents: tally.ambiguous_cents || 0,
    },
    {
      action_type: 'credits_expected',
      count: tally.credit_pending || 0,
      dollars_cents: tally.credit_pending_cents || 0,
    },
    {
      action_type: 'memo_gap',
      count: tally.tier_3_no_po || 0,
      dollars_cents: tally.tier_3_no_po_cents || 0,
    },
    {
      action_type: 'tier_2_pending',
      count: tally.tier_2 || 0,
      dollars_cents: tally.tier_2_pending_cents || 0,
    },
  ];

  return candidates
    .filter((c) => c.count > 0)
    .map((c) => ({
      key: `${shop.shop_id}:${c.action_type}`,
      shop_id: shop.shop_id,
      shop_name: shop.shop_name || `Shop ${shop.shop_id}`,
      action_type: c.action_type,
      action_label: ACTION_LABEL[c.action_type],
      count: c.count,
      dollars_cents: c.dollars_cents,
      urgency: computeUrgency(c.action_type, c.dollars_cents),
      period_start: periodStart,
      period_end: periodEnd,
    }));
}

export default function Today() {
  const navigate = useNavigate();

  // Period defaults to MTD; user can override via PeriodPicker.
  const { periodStart, periodEnd, setPeriod } = usePeriodState('_today');

  // Routing list (shops the user can see).
  const [shops, setShops] = useState([]);
  const [shopsLoading, setShopsLoading] = useState(true);
  const [shopsError, setShopsError] = useState(null);

  // Per-shop close fetch results: { [shop_id]: { close|null, error? } }
  const [closesByShop, setClosesByShop] = useState({});
  const [closesLoading, setClosesLoading] = useState(false);

  // ── On mount, default to MTD if URL/storage didn't already supply a range.
  useEffect(() => {
    if (!periodStart || !periodEnd) {
      const r = mtdRange();
      setPeriod(r.start, r.end);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load routing once.
  useEffect(() => {
    let alive = true;
    setShopsLoading(true);
    setShopsError(null);
    listRouting()
      .then((data) => {
        if (!alive) return;
        setShops((data?.shops || []).filter((s) => s.shop_id !== 7515));
      })
      .catch((err) => {
        if (!alive) return;
        setShopsError(err?.message || 'Failed to load shop list');
      })
      .finally(() => alive && setShopsLoading(false));
    return () => { alive = false; };
  }, []);

  // ── Fan-out: for each shop, fetch close in parallel.
  // 404 → shop has no close run yet for this period (rendered as "needs first run").
  useEffect(() => {
    if (!shops.length || !periodStart || !periodEnd) {
      setClosesByShop({});
      return;
    }
    let alive = true;
    setClosesLoading(true);
    setClosesByShop({});
    Promise.allSettled(
      shops.map((s) =>
        getClose(s.shop_id, periodStart, periodEnd)
          .then((data) => ({ shop_id: s.shop_id, data }))
          .catch((err) => {
            if (/404|not found/i.test(err?.message || '')) {
              return { shop_id: s.shop_id, data: null, needsFirstRun: true };
            }
            return { shop_id: s.shop_id, error: err?.message || 'Failed to load' };
          })
      )
    ).then((results) => {
      if (!alive) return;
      const next = {};
      for (const r of results) {
        if (r.status === 'fulfilled') {
          const v = r.value;
          next[v.shop_id] = {
            close: v.data || null,
            error: v.error || null,
            needsFirstRun: !!v.needsFirstRun,
          };
        }
      }
      setClosesByShop(next);
      setClosesLoading(false);
    });
    return () => { alive = false; };
  }, [shops, periodStart, periodEnd]);

  // ── Flatten action queue across all shops, sort by descending $ impact.
  const actions = useMemo(() => {
    const rows = [];
    for (const s of shops) {
      const entry = closesByShop[s.shop_id];
      if (!entry || !entry.close) continue;
      rows.push(...buildActionsForShop(s, entry.close, periodStart, periodEnd));
    }
    rows.sort((a, b) => (b.dollars_cents || 0) - (a.dollars_cents || 0));
    return rows;
  }, [shops, closesByShop, periodStart, periodEnd]);

  // Shops in this period with no close run yet — surfaced separately so the
  // user knows to run one before relying on the queue being complete.
  const shopsNeedingFirstRun = useMemo(() => {
    return shops.filter((s) => closesByShop[s.shop_id]?.needsFirstRun);
  }, [shops, closesByShop]);

  // Per-shop fetch errors (rare — Promise.allSettled isolates these).
  const shopsWithErrors = useMemo(() => {
    return shops
      .filter((s) => closesByShop[s.shop_id]?.error)
      .map((s) => ({ ...s, errorMessage: closesByShop[s.shop_id].error }));
  }, [shops, closesByShop]);

  const totalDollars = useMemo(
    () => actions.reduce((sum, a) => sum + (a.dollars_cents || 0), 0),
    [actions]
  );

  const handleRowClick = (row) => {
    const filter = ACTION_DRILLDOWN_FILTER[row.action_type] || '';
    const qs = new URLSearchParams({
      start: row.period_start,
      end: row.period_end,
      filter,
    }).toString();
    navigate(`/mec/location/${row.shop_id}?${qs}`);
  };

  return (
    <div>
      <PageHeader
        title="Today — Action Queue"
        description="What needs attention across all your shops this month. Each row is one shop + one action type, sorted by dollar impact."
      />

      {/* Period picker */}
      <Card className="mb-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <PeriodPicker
            start={periodStart}
            end={periodEnd}
            onChange={(s, e) => setPeriod(s, e)}
          />
          <div className="inline-flex items-center gap-2 text-[12px] text-[var(--color-warm-600)]">
            <Building2 size={13} className="text-[var(--color-warm-500)]" />
            <span>
              Across <span className="font-semibold text-[var(--color-warm-900)]">{shops.length}</span> {shops.length === 1 ? 'shop' : 'shops'}
            </span>
            {actions.length > 0 && (
              <Badge tone="action" size="xs">
                {formatMoney(totalDollars / 100)} total
              </Badge>
            )}
          </div>
        </div>
      </Card>

      {/* Shop-list load error (the only fully blocking failure) */}
      {shopsError && (
        <Card className="mb-5">
          <div className="flex items-start gap-3 py-1 text-[var(--color-danger-700)]">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <div>
              <div className="text-[14px] font-semibold">Couldn't load shop list.</div>
              <div className="text-[12px] mt-1 text-[var(--color-warm-600)]">{shopsError}</div>
            </div>
          </div>
        </Card>
      )}

      {/* Loading state */}
      {(shopsLoading || closesLoading) && (
        <Card className="mb-5">
          <div className="flex items-center gap-2 justify-center py-8 text-[13px] text-[var(--color-warm-500)]">
            <Loader2 size={15} className="animate-spin" />
            Aggregating across {shops.length || '…'} {shops.length === 1 ? 'shop' : 'shops'}…
          </div>
        </Card>
      )}

      {/* Action queue */}
      {!shopsLoading && !closesLoading && !shopsError && (
        <>
          {actions.length === 0 && shopsNeedingFirstRun.length === 0 && (
            <Card className="mb-5">
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="w-12 h-12 rounded-[var(--radius-md)] bg-[var(--color-recovery-50)] text-[var(--color-recovery-600)] flex items-center justify-center mb-3">
                  <CheckCircle2 size={22} />
                </div>
                <div className="text-[15px] font-semibold text-[var(--color-warm-900)]">
                  Nothing needs attention — all your shops look clean for this period.
                </div>
                <div className="text-[12px] text-[var(--color-warm-500)] mt-1.5 max-w-md">
                  No mismatched bills, no over-billing flags, no pending credits, no memo gaps,
                  no Tier 2 review backlog.
                </div>
              </div>
            </Card>
          )}

          {actions.length > 0 && (
            <div className="space-y-2.5 mb-5 stagger-children">
              {actions.map((row) => (
                <ActionQueueRow
                  key={row.key}
                  shop_name={row.shop_name}
                  shop_id={row.shop_id}
                  action_type={row.action_type}
                  action_label={row.action_label}
                  count={row.count}
                  dollars_cents={row.dollars_cents}
                  urgency={row.urgency}
                  period_start={row.period_start}
                  period_end={row.period_end}
                  onClick={() => handleRowClick(row)}
                />
              ))}
            </div>
          )}

          {/* Shops that haven't been closed yet for this period */}
          {shopsNeedingFirstRun.length > 0 && (
            <Card className="mb-5 bg-[var(--color-cream-100)] border-[var(--color-warm-200)]">
              <div className="flex items-start gap-3 py-1">
                <ListChecks size={18} className="shrink-0 mt-0.5 text-[var(--color-warm-500)]" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[var(--color-warm-900)]">
                    {shopsNeedingFirstRun.length} {shopsNeedingFirstRun.length === 1 ? 'shop hasn\'t' : 'shops haven\'t'} been closed for this period yet.
                  </div>
                  <div className="text-[12px] text-[var(--color-warm-600)] mt-1">
                    The action queue above only reflects shops with a close run for {periodStart} → {periodEnd}.
                    Run a close from the Dashboard to include these:
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {shopsNeedingFirstRun.map((s) => (
                      <Badge key={s.shop_id} tone="cream" size="xs">
                        {s.shop_name || `Shop ${s.shop_id}`}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Per-shop errors (rare; isolated by Promise.allSettled) */}
          {shopsWithErrors.length > 0 && (
            <Card className="mb-5 bg-[var(--color-danger-50)] border-[var(--color-danger-100)]">
              <div className="flex items-start gap-3 py-1">
                <AlertTriangle size={18} className="shrink-0 mt-0.5 text-[var(--color-danger-600)]" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[var(--color-danger-700)]">
                    Couldn't load {shopsWithErrors.length} {shopsWithErrors.length === 1 ? 'shop' : 'shops'} — other shops above are still accurate.
                  </div>
                  <ul className="text-[12px] text-[var(--color-warm-700)] mt-1.5 space-y-0.5">
                    {shopsWithErrors.map((s) => (
                      <li key={s.shop_id}>
                        <span className="font-semibold">{s.shop_name || `Shop ${s.shop_id}`}:</span>{' '}
                        {s.errorMessage}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
